import { useState, useEffect } from 'react';
import { validateToken as apiValidateToken, CustomerInfo, RequiredDocumentInfo } from '../services/api';
import {
  CustomerType,
  OnboardingFormData,
  PersonaFisicaForm as PFData,
  PessoaJuridicaForm as PJData,
  CreditQuestionnaireForm as CreditData,
} from '../types';

// Tipo de erro retornado pela validação de token
export type TokenErrorType = 'TOKEN_EXPIRED' | 'TOKEN_CONSUMED' | 'TOKEN_INVALID' | null;

export interface UseTokenValidationReturn {
  isLoading: boolean;
  error: string | null;
  errorType: TokenErrorType;
  customerInfo: CustomerInfo | null;
  customerType: CustomerType;
  requiredDocuments: RequiredDocumentInfo[];
  initialFormData: Partial<OnboardingFormData> | null;
}

/**
 * Hook para validação do token JWT na montagem do componente.
 * Retorna dados do cliente e documentos requeridos após validação.
 */
export function useTokenValidation(token: string | null): UseTokenValidationReturn {
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [errorType, setErrorType] = useState<TokenErrorType>(null);
  const [customerInfo, setCustomerInfo] = useState<CustomerInfo | null>(null);
  const [customerType, setCustomerType] = useState<CustomerType>('PF');
  const [requiredDocuments, setRequiredDocuments] = useState<RequiredDocumentInfo[]>([]);
  const [initialFormData, setInitialFormData] = useState<Partial<OnboardingFormData> | null>(null);

  useEffect(() => {
    const validateToken = async () => {
      if (!token) {
        setError('Token de acesso não fornecido');
        setErrorType('TOKEN_INVALID');
        setIsLoading(false);
        return;
      }

      try {
        const response = await apiValidateToken(token);

        if (!response.valid) {
          // Token inválido - mostrar erro específico
          setErrorType(response.error || 'TOKEN_INVALID');
          switch (response.error) {
            case 'TOKEN_EXPIRED':
              setError('O link de acesso expirou. Solicite um novo link ao seu representante comercial.');
              break;
            case 'TOKEN_CONSUMED':
              setError('Este link já foi utilizado. Cada link pode ser usado apenas uma vez.');
              break;
            default:
              setError('Token de acesso inválido. Verifique o link recebido.');
          }
          setIsLoading(false);
          return;
        }

        // Token válido - configurar dados do cliente
        if (response.customer) {
          setCustomerInfo(response.customer);
          const type = response.customer.type as CustomerType;
          setCustomerType(type);

          // Pré-preencher dados conhecidos do cliente
          const prefilled: Partial<OnboardingFormData> = {
            customerType: type,
            ...(type === 'PF' && {
              pessoaFisica: {
                identification: {
                  fullName: response.customer?.name || '',
                  cpf: response.customer?.cpf_cnpj || '',
                },
              } as PFData,
            }),
            ...(type === 'PJ' && {
              pessoaJuridica: {
                identification: {
                  companyName: response.customer?.name || '',
                  cnpj: response.customer?.cpf_cnpj || '',
                },
              } as PJData,
            }),
            creditQuestionnaire: {} as CreditData,
            authorizations: {
              scr: false,
              dataSharing: false,
              foreignExchange: false,
              ccs: false,
              antiCorruption: false,
              callRecording: false,
              derivatives: false,
              fraudPrevention: false,
              lgpd: false,
              registrationUpdate: false,
              allAccepted: false,
              acceptanceDate: '',
              ipAddress: '',
            },
            documents: { documents: [] },
            status: 'draft',
          };
          setInitialFormData(prefilled);
        }

        // Configurar documentos requeridos da API
        if (response.form_config?.required_documents) {
          setRequiredDocuments(response.form_config.required_documents);
        }

        setIsLoading(false);
      } catch (err) {
        console.error('Erro ao validar token:', err);
        setError('Erro ao validar o token de acesso. Tente novamente.');
        setErrorType('TOKEN_INVALID');
        setIsLoading(false);
      }
    };

    validateToken();
  }, [token]);

  return {
    isLoading,
    error,
    errorType,
    customerInfo,
    customerType,
    requiredDocuments,
    initialFormData,
  };
}
